import { Appeal, BenefitType, UserRole } from '@scts/models';
import { Accordion } from 'react-bootstrap';
import DocumentComponent from '../documents/document-component';
import {
  FormItem,
  ErrorSimple,
  Timeline,
  PageHeaderSg,
} from '@scts-ui/component-library';
import HearingComponent from '../hearing/hearing';
import Parties from '../parties/parties';
import {
  caseRefReplaceSlashWithDash,
  constructDocumentApiUrl,
  getEnumKeyByEnumValue,
  standardDateFormatter,
  useTitleService,
} from '@scts/utilities';
import {
  AuthContextType,
  useAuthContext,
} from '../../contexts/auth/auth-context';
import MemberComponent from '../parties/member';
import { DownloadLink } from '../download/download-link';
import { useConfigLoader } from '@scts/utilities';
import { ReactNode, useEffect, useState } from 'react';

interface AppealProps {
  appeal: Appeal | undefined;
}

const AppealComponent = (appealProps: AppealProps) => {
  useTitleService(
    'Appeal ' + appealProps.appeal?.caseReference + ': Social Security Chamber'
  );
  const { userRole } = useAuthContext() as AuthContextType;
  const config = useConfigLoader();
  const [timelinesUpdated, setTimelinesUpdated] = useState(false);

  useEffect(() => {
    if (appealProps.appeal?.timeLines && config.data) {
      const timelines: ReactNode[] = [];
      appealProps.appeal.timeLines.forEach((timeline) => {
        //the details prop is in the format "A new document 'Case File.pdf' has been added to your appeal" .. IJS guarantee this for type create document
        if (timeline.caseActivityType.toLowerCase() === 'create document') {
          const doc = appealProps.appeal?.documents.find(
            (d) => timeline.details.split("'")[1]
          );
          let reactNode: ReactNode;
          if (doc && appealProps.appeal) {
            //create new download link to pass down to component
            reactNode = (
              <DownloadLink
                apiUrl={constructDocumentApiUrl(
                  config?.data.apiUrl,
                  caseRefReplaceSlashWithDash(appealProps.appeal.caseReference),
                  doc.publicDocumentId
                )}
                fileName={doc.filename}
              />
            );
            timeline.download = reactNode;
          }
          timelines.push(reactNode);
        }
        setTimelinesUpdated(true);
      });
    }
  }, [config.data]);

  return (
    <div className="my-appeal-wrapper">
      <PageHeaderSg headerText={'Appeal'} />

      <div className="card">
        {appealProps.appeal && (
          <div className="card-body ">
            <FormItem
              header={'Case Reference: '}
              detail={appealProps.appeal.caseReference}
              minWidthHeader={'9.7rem'}
              minWidthDetail={'9.7rem'}
            />

            <FormItem
              header={'Appeal Received: '}
              detail={standardDateFormatter(
                new Date(appealProps.appeal.appealReceived)
              )}
              minWidthHeader={'9.7rem'}
              minWidthDetail={'9.7rem'}
            />
            <FormItem
              header={'Benefit: '}
              detail={getEnumKeyByEnumValue(
                BenefitType,
                appealProps.appeal.benefit
              )}
              minWidthHeader={'9.7rem'}
              minWidthDetail={'9.7rem'}
            />
            <FormItem
              header={'Status: '}
              detail={appealProps.appeal.status}
              minWidthHeader={'9.7rem'}
              minWidthDetail={'9.7rem'}
            />
          </div>
        )}
      </div>

      {appealProps.appeal && config.data && (
        <Accordion>
          {userRole === UserRole.Appellant && (
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <i className="bi bi-people pe-2 fs-4"></i> Appeal Parties
              </Accordion.Header>
              <Accordion.Body>
                <Parties parties={appealProps.appeal.parties} />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {appealProps.appeal.timeLines &&
            appealProps.appeal.timeLines.length > 0 &&
            userRole === UserRole.Appellant && (
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <i className="bi bi-clock-history pe-2 fs-4"></i> Appeal
                  Updates
                </Accordion.Header>
                <Accordion.Body>
                  {appealProps.appeal.timeLines && timelinesUpdated && (
                    <Timeline
                      timeLines={appealProps.appeal.timeLines.sort(
                        (objA, objB) => {
                          return (
                            new Date(objB.dateTime).getTime() -
                            new Date(objA.dateTime).getTime()
                          );
                        }
                      )}
                    />
                  )}
                </Accordion.Body>
              </Accordion.Item>
            )}

          {appealProps.appeal.members &&
            appealProps.appeal.members.length > 0 &&
            userRole === UserRole.Member && (
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <i className="bi bi-people pe-2 fs-4"></i> Appeal Members
                </Accordion.Header>
                <Accordion.Body>
                  {appealProps.appeal.members && (
                    <MemberComponent members={appealProps.appeal.members} />
                  )}
                </Accordion.Body>
              </Accordion.Item>
            )}

          {appealProps.appeal.documents.length > 0 && (
            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <i className="bi bi-file-earmark-text pe-2 fs-4"></i> Appeal
                Documents
              </Accordion.Header>
              <Accordion.Body>
                <DocumentComponent
                  documents={appealProps.appeal.documents.sort((objA, objB) => {
                    return (
                      new Date(objB.createdOn).getTime() -
                      new Date(objA.createdOn).getTime()
                    );
                  })}
                  caseRefDashes={caseRefReplaceSlashWithDash(
                    appealProps.appeal.caseReference
                  )}
                />
              </Accordion.Body>
            </Accordion.Item>
          )}

          {appealProps.appeal.hearing && (
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <i className="bi bi-building pe-2 fs-4"></i> Hearing Details
              </Accordion.Header>
              <Accordion.Body>
                <HearingComponent hearing={appealProps.appeal.hearing} />
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      )}

      {!appealProps.appeal && (
        <ErrorSimple
          icon="bi-exclamation-square-fill"
          margin="15% 30%"
          message="Either you do not have access or this case does not exist. Please contact support."
          padding="0.75rem"
          iconFontSize={'2.9rem'}
          textFontSize={'1rem'}
        />
      )}
    </div>
  );
};

export default AppealComponent;
